import React from "react";
import { ShieldCheck, ShieldAlert, ShieldX, CheckCircle2, XCircle, AlertTriangle, AlertOctagon } from "lucide-react";

export const GRADE_C = { A: "#2E7D32", B: "#4C8C2B", C: "#E39629", D: "#D55816", E: "#A5300F" };

export const SPOOF = {
  protected: { color: "#2E7D32", icon: ShieldCheck, label: "Protected" },
  partial: { color: "#E39629", icon: ShieldAlert, label: "Partially protected" },
  spoofable: { color: "#A5300F", icon: ShieldX, label: "Spoofable" },
};

export function SpoofChip({ verdict }) {
  const s = SPOOF[verdict] || { color: "#9AA0A6", icon: ShieldAlert, label: "Not scanned" };
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded"
      data-testid="spoof-chip"
      style={{ color: s.color, background: `${s.color}14`, border: `1px solid ${s.color}33` }}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
}

export function RecordStatus({ ok, warn, label, value }) {
  const color = ok ? "#2E7D32" : warn ? "#E39629" : "#A5300F";
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  return (
    <div className="flex items-start gap-2.5 p-3 rounded-md border border-[#ECE6DD] bg-white" data-testid={`record-${label}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">{label}</div>
        <div className="text-sm font-semibold text-[#323232] break-words">{value}</div>
      </div>
    </div>
  );
}

export function TakeoverBadge({ takeover }) {
  if (!takeover) return <span className="text-xs text-[#B5AC9E]">—</span>;
  if (takeover.vulnerable)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded text-white" style={{ background: "#A5300F" }} data-testid="takeover-vuln">
      <AlertOctagon className="w-3 h-3" /> Takeover: {takeover.service || "risk"}
    </span>;
  if (takeover.dangling)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded" style={{ color: "#E39629", background: "#E3962914", border: "1px solid #E3962933" }}>
      <AlertTriangle className="w-3 h-3" /> Dangling CNAME
    </span>;
  return <span className="text-xs text-[#2E7D32]">Clear</span>;
}

// Lightweight SVG area+line trend chart for posture scores over time.
export function ScoreTrend({ points, w = 640, h = 120 }) {
  if (!points || points.length < 2)
    return <div className="text-xs text-[#B5AC9E] py-6 text-center">Run at least two scans to see a trend.</div>;
  const pad = 8;
  const xs = (i) => pad + (i * (w - 2 * pad)) / (points.length - 1);
  const ys = (v) => pad + (1 - (v ?? 0) / 100) * (h - 2 * pad);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${xs(i)},${ys(p.score)}`).join(" ");
  const area = `${line} L${xs(points.length - 1)},${h - pad} L${xs(0)},${h - pad} Z`;
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} data-testid="score-trend" className="overflow-visible">
      <defs>
        <linearGradient id="trendfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A5300F" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#A5300F" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#trendfill)" />
      <path d={line} fill="none" stroke="#A5300F" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <circle key={p.date ?? i} cx={xs(i)} cy={ys(p.score)} r="3" fill={GRADE_C[p.grade] || "#A5300F"} stroke="#fff" strokeWidth="1.5" />
      ))}
    </svg>
  );
}
